import React, { Component } from 'react';

import classnames from 'classnames';
import { booleanFromString } from 'helpers/String';
import identity from 'lodash/identity';
import intersection from 'lodash/intersection';
import isUndefined from 'lodash/isUndefined';
import { UiService } from 'modules/ui';
import PropTypes from 'prop-types';

import FormContext from './FormContext';
import { validate, CONDITIONS_PROPS } from './Validation';

import './Form.scss';

export default class Form extends Component {
  // #region setup
  static Context = FormContext;

  static propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    onSubmit: PropTypes.func,
    onSubmitAndReturn: PropTypes.func,
    onChange: PropTypes.func,
    scrollOnError: PropTypes.bool,
    onValidate: PropTypes.func
  };

  static defaultProps = {
    scrollOnError: true
  };

  constructor(props) {
    super(props);

    this.state = {
      errors: {},
      inputs: {},
      restrictions: {},
      submitted: false
    };

    this.actions = {
      clear: this.clear.bind(this),
      setField: this.setField.bind(this),
      setValue: this.setValue.bind(this)
    };

    const { onSubmit, onSubmitAndReturn } = this.props;

    if (onSubmit) {
      this.actions.submit = this.submit.bind(this);
    }

    if (onSubmitAndReturn) {
      this.actions.submitAndReturn = this.submitAndReturn.bind(this);
    }

    this.getters = {};
  }

  getStateInputs = () => {
    return this.state.inputs;
  };

  setField(props) {
    const safeProps = props || {};

    this.setFieldGetter(safeProps.name, safeProps.getter);
    this.addRestrictions(safeProps.name, safeProps);
    this.setValue({
      target: {
        name: safeProps.name,
        value: safeProps.value,
        type: safeProps.type
      }
    });
  }

  addRestrictions = (name, props) => {
    const { restrictions } = this.state;

    const valuesRestrictionsProps = Object.values(CONDITIONS_PROPS);
    const keysProps = Object.keys(props);

    const hasRestriction = valuesRestrictionsProps.some((v) =>
      keysProps.includes(v)
    );

    if (hasRestriction) {
      restrictions[name] = {};

      const intersectionRestrictions = intersection(
        valuesRestrictionsProps,
        keysProps
      );

      intersectionRestrictions.forEach((propName) => {
        const sanitizedProp = booleanFromString(props[propName]);

        restrictions[name][propName] =
          props[propName] || Boolean(sanitizedProp);
      });
    }

    this.setState({ restrictions });
  };

  /**
   * @method
   * Set a getter function to the field given by `name`.
   * This getter function is used to get a field value when running validation.
   * @param {string} name - Field name.
   * @param {function} getter - Function to be applied over the field value.
   */
  setFieldGetter(name, getter) {
    if (typeof getter !== 'function') {
      return;
    }

    this.getters[name] = getter;
  }

  /**
   * @method
   * Get field getter given by `name`.
   * If none was provided, the identity function is returned.
   * @param {string} name - Field name.
   * @returns {function}
   */
  getFieldGetter(name) {
    return this.getters[name] || identity;
  }

  // #endregion

  get isFormDisabled() {
    return this.state.submitted && this.isFormInvalid;
  }

  get isFormInvalid() {
    const { errors } = this.state;

    const isFormInvalid = Object.keys(errors).reduce(
      (isFormInvalid, fieldName) => {
        const isFieldInvalid = Object.keys(errors[fieldName]).reduce(
          (isFieldInvalid, validationName) =>
            isFieldInvalid || errors[fieldName][validationName],
          false
        );

        return isFormInvalid || isFieldInvalid;
      },
      false
    );

    return isFormInvalid;
  }

  get className() {
    const { className } = this.props;

    return classnames('form', className);
  }
  // #endregion

  // #region setters
  setValue = ({ target: { name, value, type, checked } }) => {
    const { onChange } = this.props;
    const { inputs, restrictions } = this.state;

    inputs[name] = type === 'checkbox' ? checked : value;

    this.checkFieldValidity(name, inputs[name], restrictions[name]);
    this.setState({ inputs });
    onChange && onChange(this.state.inputs);
  };

  setSubmitted(submitted = true) {
    this.setState({ submitted });
  }
  // #endregion

  // #region actions
  submitAction = (event) => {
    event?.preventDefault();

    const { onValidate } = this.props;

    this.validateForm();
    this.setSubmitted();

    const postValidation = onValidate ? onValidate() : true;

    if (this.isFormInvalid) {
      const { errors } = this.state;
      const nameElementsWithErrors = Object.keys(errors).filter((fieldName) => {
        const currentField = errors[fieldName];
        const currentFieldProps = Object.keys(currentField);

        return currentFieldProps.some((propName) => currentField[propName]);
      });

      if (this.props.scrollOnError && nameElementsWithErrors.length > 0) {
        const element = `[name="${nameElementsWithErrors[0]}"]`;

        UiService.smoothScroll(element);
      }

      return false;
    }

    return postValidation;
  };

  submit = async (event) => {
    const { onSubmit } = this.props;

    const response = this.submitAction(event);

    return onSubmit && response && onSubmit(this.state.inputs);
  };

  submitAndReturn = async (event) => {
    const { onSubmitAndReturn } = this.props;
    const validation = this.submitAction(event);
    let response = false;

    if (onSubmitAndReturn && validation) {
      response = await onSubmitAndReturn(this.state.inputs);
    }

    return response;
  };

  clear = () => {
    Object.keys(this.state.inputs).forEach((key) => {
      this.setState({ inputs: { [key]: '' } });
    });
  };
  // #endregion

  // #region validations
  checkFieldValidity(name, value, restrictions) {
    if (this.fieldHasRestrictions(name)) {
      const fieldGetter = this.getFieldGetter(name);

      const errors = {
        [name]: validate(restrictions, fieldGetter(value))
      };

      this.setState((prevState) => ({
        errors: { ...prevState.errors, ...errors }
      }));
    }
  }

  validateForm() {
    const { restrictions, inputs } = this.state;

    Object.keys(restrictions).forEach((key) =>
      this.checkFieldValidity(key, inputs[key], restrictions[key])
    );
  }

  fieldHasRestrictions(name) {
    return !isUndefined(this.state.restrictions[name]);
  }
  // #endregion

  render() {
    return (
      <div className={this.className}>
        <FormContext.Provider
          value={{
            ...this.actions,
            state: this.state,
            isFormDisabled: this.isFormDisabled
          }}
        >
          {this.props.children}
        </FormContext.Provider>
      </div>
    );
  }
}
